import { useCallback, useEffect, useState } from 'react';

const isRunningStandalone = () => {
    if (typeof window === 'undefined') {
        return false;
    }

    return Boolean(
        window.matchMedia?.('(display-mode: standalone)').matches ||
        window.navigator.standalone
    );
};

const isIosDevice = () => {
    if (typeof window === 'undefined') {
        return false;
    }

    const userAgent = window.navigator.userAgent || '';
    const platform = window.navigator.platform || '';

    return /iphone|ipad|ipod/i.test(userAgent) ||
        (platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
};

export default function useInstallPrompt() {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isInstalled, setIsInstalled] = useState(() => isRunningStandalone());
    const [isIos, setIsIos] = useState(false);

    useEffect(() => {
        setIsInstalled(isRunningStandalone());
        setIsIos(isIosDevice());

        const handleBeforeInstallPrompt = (event) => {
            event.preventDefault();
            setDeferredPrompt(event);
            setIsInstalled(false);
        };

        const handleAppInstalled = () => {
            setDeferredPrompt(null);
            setIsInstalled(true);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    const installApp = useCallback(async () => {
        if (!deferredPrompt) {
            return { outcome: 'unavailable' };
        }

        const promptEvent = deferredPrompt;
        setDeferredPrompt(null);

        try {
            const promptResult = promptEvent.prompt();
            const choiceResult = promptEvent.userChoice
                ? await promptEvent.userChoice
                : await promptResult;

            if (choiceResult?.outcome === 'accepted') {
                setIsInstalled(true);
            }

            return choiceResult;
        } catch (error) {
            setDeferredPrompt(promptEvent);
            throw error;
        }
    }, [deferredPrompt]);

    return {
        canInstall: Boolean(deferredPrompt) && !isInstalled,
        installApp,
        isInstalled,
        isIos,
    };
}
